import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { useMemo, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useResponsables } from "../datos/useResponsables";
import {
  comisionesPorPersona,
  diasMes,
  euros,
  mesDe,
  textoComision,
} from "../dominio/comisiones";
import { filtrarPorPersona, TODAS } from "../dominio/equipo";
import type { ComisionFila } from "../dominio/tipos";
import { formatoFecha } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { FiltroPersonaSelect } from "./FiltroPersonaSelect";
import { useFiltroPersona } from "./useFiltroPersona";
import { PaginaAdmin, SelectorMes } from "./PaginaAdmin";

/**
 * Comisiones (S9, Jan y Robert 06-07/10/2026; 40, Robert 23/10/2026): la comisión de cada persona
 * por cada cobro del mes (setter 5 % y consultor 10 % de la base sin IVA, con la ventana de 6 meses) y
 * la suma por persona y papel. «Comisión pagada», por persona. Qué pasa con las devoluciones está
 * pendiente de decidir: aquí no hay liquidaciones. Solo administradores.
 */
export const ComisionesPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const esAdmin = useEsAdmin();
  const abrirFicha = useAbrirFicha();
  const { nombre } = useResponsables();
  const [mes, setMes] = useState(() => mesDe(new Date()));
  const [persona, setPersona] = useFiltroPersona("comisiones");
  const rango = diasMes(mes);

  const {
    data: comisiones,
    isPending,
    refetch,
  } = useGetList<ComisionFila>(
    "comisiones_vista",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "cobrado_el", order: "ASC" },
      filter: {
        "cobrado_el@gte": rango.desde,
        "cobrado_el@lt": rango.hasta,
      },
    },
    { enabled: esAdmin },
  );
  const filtradas = useMemo(
    () =>
      persona === TODAS
        ? (comisiones ?? [])
        : filtrarPorPersona(
            (comisiones ?? []).map((k) => ({ ...k, asignado_a: k.sale_id })),
            persona,
          ),
    [comisiones, persona],
  );
  const resumen = comisionesPorPersona(filtradas);
  const marcarPagada = async (k: ComisionFila, pagada: boolean) => {
    try {
      await dataProvider.marcarComision(k.id, pagada);
      notify(
        pagada
          ? "nevora.comisiones.marcada_pagada"
          : "nevora.comisiones.desmarcada_pagada",
        { type: "success" },
      );
      await refetch();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    }
  };
  const total = resumen.reduce((s, r) => s + r.comision, 0);

  const celda = "px-2 py-2 text-right tabular-nums whitespace-nowrap";

  return (
    <PaginaAdmin
      titulo="nevora.comisiones.titulo"
      ayuda="nevora.comisiones.ayuda"
      acciones={
        <div className="flex flex-wrap items-center gap-2">
          <FiltroPersonaSelect valor={persona} cambiar={setPersona} />
          <SelectorMes mes={mes} cambiar={setMes} />
        </div>
      }
    >
      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="titulo-seccion">
          {translate("nevora.comisiones.por_persona")}
        </h2>
        {resumen.length ? (
          <table className="w-full text-sm" data-testid="comisiones-resumen">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="px-2 py-2 text-left font-medium">
                  {translate("nevora.equipo.persona")}
                </th>
                <th className="px-2 py-2 text-left font-medium">
                  {translate("nevora.comisiones.papel")}
                </th>
                <th className={`${celda} font-medium`}>
                  {translate("nevora.comisiones.cobros")}
                </th>
                <th className={`${celda} font-medium`}>
                  {translate("nevora.comisiones.base")}
                </th>
                <th className={`${celda} font-medium`}>
                  {translate("nevora.comisiones.comision")}
                </th>
              </tr>
            </thead>
            <tbody>
              {resumen.map((r) => (
                <tr key={`${r.para}|${r.papel}`} className="border-b">
                  <td className="px-2 py-2 font-medium">{nombre(r.para)}</td>
                  <td className="px-2 py-2">
                    {translate(`nevora.comisiones.papeles.${r.papel}`)}
                  </td>
                  <td className={celda}>{r.cobros}</td>
                  <td className={celda}>{euros(r.base)}</td>
                  <td className={`${celda} font-medium`}>
                    {euros(r.comision)}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="px-2 py-2 font-medium" colSpan={4}>
                  {translate("nevora.comisiones.total")}
                </td>
                <td className={`${celda} font-semibold`}>{euros(total)}</td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("nevora.comisiones.sin_comisiones")}
          </p>
        )}
      </section>

      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="titulo-seccion">
          {translate("nevora.comisiones.cobros_mes")}
        </h2>
        {isPending ? null : filtradas.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="comisiones-cobros">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="px-2 py-2 text-left font-medium">
                    {translate("nevora.cobro.fecha")}
                  </th>
                  <th className="px-2 py-2 text-left font-medium">
                    {translate("resources.negocios.fields.nombre")}
                  </th>
                  <th className={`${celda} font-medium`}>
                    {translate("nevora.comisiones.base")}
                  </th>
                  <th className="px-2 py-2 text-left font-medium">
                    {translate("nevora.comisiones.comision")}
                  </th>
                  <th className="px-2 py-2 text-left font-medium">
                    {translate("nevora.comisiones.pagada")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((k) => (
                  <tr key={String(k.id)} className="border-b last:border-b-0">
                    <td className="px-2 py-2 whitespace-nowrap">
                      {formatoFecha(k.cobrado_el)}
                    </td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        className="font-medium underline-offset-2 hover:underline text-left"
                        onClick={() => abrirFicha(k.negocio_id, k.proyecto_id)}
                      >
                        {k.nombre}
                      </button>
                      <span className="block text-xs text-muted-foreground">
                        {translate("nevora.proyectos.cobro_n", {
                          n: k.numero,
                        })}
                        {k.concepto === "mantenimiento"
                          ? ` · ${translate("nevora.cobro.concepto_mantenimiento")}`
                          : ""}
                      </span>
                    </td>
                    <td className={celda}>{euros(Number(k.base))}</td>
                    <td className="px-2 py-2">
                      {textoComision(k, translate, nombre)}
                    </td>
                    <td className="px-2 py-2">
                      {k.sale_id != null && !k.motivo ? (
                        <label className="flex items-center gap-2 whitespace-nowrap">
                          <Checkbox
                            checked={!!k.pagada_at}
                            onCheckedChange={(v) => marcarPagada(k, v === true)}
                            aria-label={translate("nevora.comisiones.pagada")}
                          />
                          <span className="text-xs text-muted-foreground">
                            {k.pagada_at
                              ? translate("nevora.comisiones.pagada_el", {
                                  fecha: formatoFecha(k.pagada_at),
                                })
                              : translate("nevora.comisiones.pendiente")}
                          </span>
                        </label>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("nevora.comisiones.sin_cobros")}
          </p>
        )}
      </section>
    </PaginaAdmin>
  );
};

ComisionesPage.path = "/comisiones";
